/** A failed API call, normalised from Laravel's several error shapes. */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  /** First message per field, from a 422 `errors` bag: { unit_code: "The unit code has already been taken." } */
  readonly fieldErrors: Readonly<Record<string, string>>;
  /** Seconds to wait, when the server rate-limited the call (429). */
  readonly retryAfter: number | null;

  constructor(
    code: ApiErrorCode,
    message: string,
    status: number,
    fieldErrors: Record<string, string> = {},
    retryAfter: number | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.fieldErrors = fieldErrors;
    this.retryAfter = retryAfter;
  }
}

export type ApiErrorCode =
  | "NETWORK_ERROR"
  | "UNAUTHENTICATED"
  | "FORBIDDEN"
  | "NOT_FOUND"
  | "VALIDATION"
  | "RATE_LIMITED"
  | "BAD_REQUEST"
  | "SERVER_ERROR"
  | "INVALID_RESPONSE";

const CODE_BY_STATUS: Readonly<Record<number, ApiErrorCode>> = {
  400: "BAD_REQUEST",
  401: "UNAUTHENTICATED",
  403: "FORBIDDEN",
  404: "NOT_FOUND",
  422: "VALIDATION",
  429: "RATE_LIMITED",
};

interface LaravelErrorBody {
  message?: unknown;
  errors?: unknown;
  retry_after?: unknown;
}

/** The first message of each field in a Laravel error bag ({ field: ["msg", …] } or { field: "msg" }). */
function firstFieldMessages(errors: unknown): Record<string, string> {
  if (typeof errors !== "object" || errors === null || Array.isArray(errors)) return {};
  const result: Record<string, string> = {};
  for (const [field, value] of Object.entries(errors as Record<string, unknown>)) {
    const first: unknown = Array.isArray(value) ? value[0] : value;
    if (typeof first === "string") result[field] = first;
  }
  return result;
}

export function apiErrorFrom(body: unknown, status: number, headers?: Headers): ApiError {
  const error = (typeof body === "object" && body !== null ? body : {}) as LaravelErrorBody;
  const headerRetry = Number(headers?.get("Retry-After"));
  const bodyRetry = typeof error.retry_after === "number" ? error.retry_after : null;
  return new ApiError(
    CODE_BY_STATUS[status] ?? (status >= 500 ? "SERVER_ERROR" : "BAD_REQUEST"),
    typeof error.message === "string" && error.message !== "" ? error.message : `HTTP ${status}`,
    status,
    firstFieldMessages(error.errors),
    bodyRetry ?? (Number.isFinite(headerRetry) && headerRetry > 0 ? headerRetry : null),
  );
}

export function networkError(): ApiError {
  return new ApiError("NETWORK_ERROR", "Network request failed", 0);
}

export function isApiError(error: unknown, ...codes: ApiErrorCode[]): error is ApiError {
  return error instanceof ApiError && (codes.length === 0 || codes.includes(error.code));
}
